import { useState, type Ref } from 'react'
import { Box, Flex, Grid, Spinner, Text } from '@chakra-ui/react'
import { LuSearch as Search, LuUser } from 'react-icons/lu'
import {
  FormSection,
  TrackedInput,
  TrackedPhoneInput,
  TrackedSelect,
  useFormColors,
  useFormContext,
  withFormProvider,
  type WithFormProvider,
} from '@minetrace/ui/forms'
import { toaster } from '@minetrace/ui'
import { personsService } from '../../../api/services'
import {
  useStep1DataForm,
  type Step1DataFormValues,
} from './useStep1DataForm'
import { PersonFieldLengths } from '../../../lib/fieldLengths'

export interface Step1LookupResult {
  /** Existing person id when found in DB; caller should redirect to edit. */
  existingPersonId: string | null
  /** Names returned by SUNAT, to be applied to the data step. */
  sunatNames?: { names: string; paternalSurname: string; maternalSurname?: string | null }
}

interface Step1DataProps {
  ref?: Ref<unknown>
  defaultValues?: Step1DataFormValues
  isEditing?: boolean
  onLookupResolved?: (result: Step1LookupResult) => void
}

const DOC_TYPE_OPTIONS = [
  { value: 1, label: 'DNI' },
  { value: 2, label: 'Carnet de Extranjería' },
  { value: 3, label: 'Pasaporte' },
]

const GENDER_OPTIONS = [
  { value: 1, label: 'Masculino' },
  { value: 2, label: 'Femenino' },
]

type InnerProps = Step1DataProps & WithFormProvider<Step1DataFormValues>

function Step1InnerComponent({ isEditing, onLookupResolved }: InnerProps) {
  const c = useFormColors()
  const { watch, getValues, setError, clearErrors, reset } = useFormContext<Step1DataFormValues>()
  const documentType = watch('documentType')
  const isDni = documentType === 1
  const [looking, setLooking] = useState(false)

  const handleSearch = async () => {
    const doc = (getValues('identityDoc') ?? '').trim()
    if (!isDni) return
    if (!/^\d{8}$/.test(doc)) {
      setError('identityDoc', { message: 'personas.nuevo.zod.dniFormat' })
      return
    }
    clearErrors('identityDoc')
    setLooking(true)
    try {
      const lookup = await personsService.lookupDni(doc, documentType)
      if (lookup.foundInDatabase && lookup.personId) {
        toaster.create({ type: 'info', description: 'Esta persona ya existe — abriendo su ficha.' })
        onLookupResolved?.({ existingPersonId: lookup.personId })
        return
      }
      if (lookup.source === 'sunat') {
        // Patch only the name fields, keep whatever the user typed elsewhere.
        const current = getValues()
        reset({
          ...current,
          names: lookup.names ?? '',
          paternalSurname: lookup.paternalSurname ?? '',
          maternalSurname: lookup.maternalSurname ?? '',
        })
        toaster.success({ description: 'Datos cargados desde SUNAT.' })
        onLookupResolved?.({
          existingPersonId: null,
          sunatNames: {
            names: lookup.names ?? '',
            paternalSurname: lookup.paternalSurname ?? '',
            maternalSurname: lookup.maternalSurname,
          },
        })
        return
      }
      toaster.create({ type: 'info', description: 'No encontramos el DNI. Continúe ingresando los datos manualmente.' })
    } catch {
      toaster.error({ description: 'Error consultando el DNI.' })
    } finally {
      setLooking(false)
    }
  }

  return (
    <FormSection
      icon={LuUser}
      titleTid="personas.nuevo.sections.infoPersonal"
    >
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
        <TrackedSelect
          name="documentType"
          label="Tipo de documento"
          options={DOC_TYPE_OPTIONS}
          placeholder="Seleccionar..."
          required
          disabled={isEditing}
        />
        {isEditing ? (
          <TrackedInput
            name="identityDoc"
            label="Número de documento"
            placeholder={isDni ? '8 dígitos' : 'Número'}
            required
            readOnly
            maxLength={PersonFieldLengths.identityDoc}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ autoComplete: 'off' } as any)}
          />
        ) : (
          <Flex gap={3} alignItems="flex-end">
            <Box flex={1}>
              <TrackedInput
                name="identityDoc"
                label="Número de documento"
                placeholder={isDni ? '8 dígitos' : 'Número'}
                required
                maxLength={PersonFieldLengths.identityDoc}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...({ autoComplete: 'off' } as any)}
              />
            </Box>
            {isDni && (
              <button
                type="button"
                onClick={handleSearch}
                disabled={looking}
                title="Buscar en SUNAT"
                style={{
                  height: '44px',
                  padding: '0 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: c.accent,
                  color: 'white',
                  borderRadius: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: looking ? 'not-allowed' : 'pointer',
                  opacity: looking ? 0.6 : 1,
                  flexShrink: 0,
                  border: 'none',
                }}
              >
                {looking ? <Spinner size="sm" /> : <Search size={16} />}
                <Text as="span">Buscar</Text>
              </button>
            )}
          </Flex>
        )}

        <TrackedInput name="names" label="Nombres" placeholder="Juan" required maxLength={PersonFieldLengths.names} />
        <TrackedInput name="paternalSurname" label="Apellido paterno" placeholder="Pérez" required maxLength={PersonFieldLengths.paternalSurname} />
        <TrackedInput name="maternalSurname" label="Apellido materno" placeholder="García" maxLength={PersonFieldLengths.maternalSurname} />
        <TrackedSelect name="gender" label="Género" options={GENDER_OPTIONS} placeholder="Seleccionar..." />
        <TrackedInput name="email" label="Email" placeholder="usuario@empresa.com" type="email" required maxLength={PersonFieldLengths.email} />
        <TrackedPhoneInput name="phoneMobile" label="Celular" placeholder="999 999 999" maxLength={PersonFieldLengths.phoneMobile} required />
        <TrackedInput name="address" label="Dirección" placeholder="Av. Lima 123" maxLength={PersonFieldLengths.address} />
      </Grid>
    </FormSection>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Step1Inner = withFormProvider(Step1InnerComponent, useStep1DataForm) as any

const Step1Data = ({ ref, ...rest }: Step1DataProps) => (
  <Step1Inner ref={ref} {...rest} />
)

export default Step1Data
